/**
 * Infrastructure category utilities
 * 
 * When the user selects category "Infrastructure", the KPI dropdown becomes
 * multi-select, populated with infrastructure names from the INFRA_NAME lookup map.
 * Data is pivoted so each school has one row with infra quantities as columns,
 * enabling side-by-side comparison of infrastructure items.
 */

import { INFRA_NAME } from '../constants/lookupMaps';

/**
 * Infrastructure name options for the KPI dropdown.
 * Derived from the INFRA_NAME constant map.
 * Each entry: { key: '<id>', label: '<infraName>' }
 */
export const INFRA_KPI_OPTIONS: Array<{ key: string; label: string }> = Object.entries(
  INFRA_NAME
).map(([id, name]) => ({
  key: id, // The numeric ID as string (e.g., '1', '2', ...)
  label: String(name),
}));

/**
 * All infra column keys (infra_1, infra_2, ..., infra_36)
 */
export const ALL_INFRA_COLUMN_KEYS: string[] = Object.keys(INFRA_NAME).map(id => `infra_${id}`);

/**
 * Column label overrides for infra columns.
 * Maps infra_1 → "Computer Lab 1", infra_2 → "Computer Lab 2", etc.
 */
export const INFRA_COLUMN_LABEL_OVERRIDES: Record<string, string> = (() => {
  const overrides: Record<string, string> = {};
  Object.entries(INFRA_NAME).forEach(([id, label]) => {
    overrides[`infra_${id}`] = String(label);
  });
  return overrides;
})();

/**
 * Check whether a category string represents the Infrastructure category.
 */
export function isInfrastructureCategory(category: string | undefined | null): boolean {
  if (!category) return false;
  const lower = category.toLowerCase().replace(/[\s_\-]/g, '');
  // Match only exact "Infrastructure" / "Infra" — NOT "Sports Infrastructure", "CWSN Infrastructure", etc.
  return lower === 'infrastructure' || 
         lower === 'infra' || 
         lower === 'infrastructureprofile' ||
         lower === 'infraprofile';
}

/**
 * Given raw KPI data items and the selected infra name ID,
 * filter kvs_details within each item to only include records whose
 * `name` field matches the selected infra ID.
 */
export function filterKpiDataByInfraName(
  kpiItems: any[],
  infraNameId: string
): any[] {
  return kpiItems
    .map((item) => {
      if (!Array.isArray(item.kvs_details)) return item;

      const filteredSchools = item.kvs_details.filter((school: any) => {
        const nameVal = school.name ?? school.infraName ?? school.infra_name ?? '';
        return String(nameVal) === String(infraNameId);
      });

      return {
        ...item,
        kvs_details: filteredSchools,
        count: filteredSchools.length,
        'School Count': filteredSchools.length,
      };
    })
    .filter((item) => item.count > 0 || (Array.isArray(item.kvs_details) && item.kvs_details.length > 0));
}

/**
 * Pivot infrastructure data: one row per school with infra quantities as columns.
 * 
 * Input: raw kvs_details array where each record has { schoolName, name (infra ID), quantity, ... }
 * Output: array of pivoted records, one per school, with infra_1, infra_2, ... columns
 * 
 * @param allSchools - Flat array of school records from kvs_details
 * @param selectedInfraIds - Optional: only include these infra columns (if empty, include all)
 */
export function pivotInfraData(
  allSchools: any[],
  selectedInfraIds?: string[]
): any[] {
  // Group by school using a composite key
  const schoolMap = new Map<string, any>();

  allSchools.forEach(school => {
    const schoolKey = String(school.kvID || school.schoolID || school.school_id || 
      `${school.schoolName || ''}_${school.stateName || ''}_${school.districtName || ''}`);

    if (!schoolMap.has(schoolKey)) {
      // Create base school record (without name/quantity which are infra-specific)
      const baseRecord: any = {};
      Object.entries(school).forEach(([key, value]) => {
        if (key !== 'name' && key !== 'quantity' && key !== 'Name' && key !== 'Quantity') {
          baseRecord[key] = value;
        }
      });
      // Initialize all infra columns to 0
      const infraIds = selectedInfraIds && selectedInfraIds.length > 0 
        ? selectedInfraIds 
        : Object.keys(INFRA_NAME);
      infraIds.forEach(id => {
        baseRecord[`infra_${id}`] = 0;
      });
      schoolMap.set(schoolKey, baseRecord);
    }

    // Set infra column value
    const infraId = String(school.name ?? school.infraName ?? school.infra_name ?? '');
    const infraKey = `infra_${infraId}`;
    const quantity = Number(school.quantity ?? school.Quantity ?? 0);
    
    // Only include if no filter or if this infra is in the selected list
    if (!selectedInfraIds || selectedInfraIds.length === 0 || selectedInfraIds.includes(infraId)) {
      schoolMap.get(schoolKey)![infraKey] = quantity;
    }
  });

  return Array.from(schoolMap.values());
}

/**
 * Aggregate infrastructure data by infra name for chart display.
 * Groups all schools across all KPI data items by their infra name field
 * and sums up quantity for each infra type.
 * 
 * @returns Array of { name: string, value: number, sortKey: number } for chart consumption
 */
export function aggregateInfraByName(
  kpiItems: any[],
  infraNameIds?: string[] | string
): Array<{ name: string; value: number; sortKey: number }> {
  const infraQuantityMap: Record<string, number> = {};
  
  // Normalize to array
  const selectedIds = infraNameIds 
    ? (Array.isArray(infraNameIds) ? infraNameIds : [infraNameIds])
    : undefined;

  kpiItems.forEach((item) => {
    if (!Array.isArray(item.kvs_details)) return;

    item.kvs_details.forEach((school: any) => {
      const nameVal = String(school.name ?? school.infraName ?? school.infra_name ?? '');
      
      // If specific infras are selected, only count those
      if (selectedIds && selectedIds.length > 0 && !selectedIds.includes(nameVal)) return;

      const quantity = Number(school.quantity ?? school.Quantity ?? 1);
      infraQuantityMap[nameVal] = (infraQuantityMap[nameVal] || 0) + quantity;
    });
  });

  return Object.entries(infraQuantityMap)
    .map(([id, totalQuantity]) => {
      const label = (INFRA_NAME as Record<string, string>)[id] || `Infra ${id}`;
      return {
        name: label,
        value: totalQuantity,
        sortKey: Number(id),
      };
    })
    .sort((a, b) => a.sortKey - b.sortKey);
}

/**
 * Default visible columns for infrastructure tables (base columns + infra columns added dynamically).
 */
export const INFRA_BASE_COLUMNS = [
  'schoolName',
  'stateName',
  'roName',
  'districtName',
  'schoolDetails.schoolName',
  'schoolDetails.stateName',
  'schoolDetails.roName',
  'schoolDetails.districtName',
];

/**
 * Build the full default visible columns list for infra with selected items.
 */
export function getInfraDefaultColumns(selectedInfraIds: string[]): string[] {
  return [
    ...INFRA_BASE_COLUMNS,
    ...selectedInfraIds.map(id => `infra_${id}`),
  ];
}
